import { Component, output, signal, computed, input, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

interface DateRange {
  from: { year: number; month: number; day: number } | null;
  to: { year: number; month: number; day: number } | null;
}

@Component({
  selector: 'app-range-date-picker',
  templateUrl: './range-date-picker.component.html',
  styleUrls: ['./range-date-picker.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule],
})
export class RangeDatePickerComponent {
  // Output event
  selectedDates = output<DateRange>();

  initialFrom = input<string>('');
  initialTo = input<string>('');
  disableFromDate = input(false);
  /** When true, the end date cannot be the same day as the start date. */
  requireEndAfterStart = input(false);

  // Signals for date values
  fromDate = signal<string>('');
  toDate = signal<string>('');

  private readonly syncInitialDates = effect(() => {
    const from = this.initialFrom();
    const to = this.initialTo();
    if (from) {
      this.fromDate.set(from);
    }
    if (to) {
      this.toDate.set(to);
    }
  });

  toDateMin = computed(() => {
    const from = this.fromDate();
    if (!this.requireEndAfterStart() || !from) {
      return from;
    }
    const [year, month, day] = from.split('-').map(Number);
    const next = new Date(year, month - 1, day + 1);
    return `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, '0')}-${String(next.getDate()).padStart(2, '0')}`;
  });

  // Computed date range
  dateRange = computed<DateRange>(() => ({
    from: this.fromDate() ? this.parseDate(this.fromDate()) : null,
    to: this.toDate() ? this.parseDate(this.toDate()) : null,
  }));

  onFromDateChange(value: string): void {
    this.fromDate.set(value);
    this.emitDates();
  }

  onToDateChange(value: string): void {
    this.toDate.set(value);
    this.emitDates();
  }

  private emitDates(): void {
    this.selectedDates.emit(this.dateRange());
  }

  private parseDate(dateString: string): { year: number; month: number; day: number } | null {
    if (!dateString) return null;
    const [year, month, day] = dateString.split('-').map(Number);
    return { year, month, day };
  }
}
